import { Activity } from "lucide-react";
import { useEffect, useState } from "react";
import { TimelineItem } from "../data/TimelineItem.js";
import { Drawer } from "../primitives/Drawer.js";
import { EmptyState } from "../primitives/EmptyState.js";
import { Skeleton } from "../primitives/Skeleton.js";
import { Tabs } from "../primitives/Tabs.js";
import { COLORS, SPACE } from "../theme/index.js";

type ActivityEntry = {
  id: string;
  actor?: string;
  action: string;
  subject?: string;
  occurred_at: string;
};

type ActivityDrawerProps = {
  onFetchActivity?: (filter: "all" | "mine") => Promise<{ entries: ActivityEntry[] }>;
};

export function ActivityDrawer({ onFetchActivity }: ActivityDrawerProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "mine">("all");
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "j") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open || !onFetchActivity) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const load = () => {
      onFetchActivity(filter)
        .then((r) => {
          if (!cancelled) {
            setEntries(r.entries);
            setLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) setLoading(false);
        });
    };
    load();
    const interval = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, filter, onFetchActivity]);

  return (
    <>
      <Drawer open={open} onClose={() => setOpen(false)} title="Activity Feed">
        <div style={{ padding: SPACE[4] }}>
          <Tabs
            tabs={[
              { id: "all", label: "All" },
              { id: "mine", label: "Mine" },
            ]}
            activeId={filter}
            onChange={(id) => setFilter(id as "all" | "mine")}
            style={{ marginBottom: SPACE[4] }}
          />
          {loading ? (
            [0, 1, 2, 3, 4].map((i) => (
              <div
                key={`skeleton-loading-${i}`}
                style={{ padding: `${SPACE[3]}px 0`, borderBottom: `1px solid ${COLORS.border}` }}
              >
                <Skeleton height={14} width="70%" style={{ marginBottom: SPACE[2] }} />
                <Skeleton height={11} width="30%" />
              </div>
            ))
          ) : entries.length === 0 ? (
            <EmptyState
              icon={<Activity size={32} />}
              title="No activity yet"
              description="Actions on deals, companies, and contacts will appear here."
            />
          ) : (
            entries.map((e) => <TimelineItem key={e.id} {...e} />)
          )}
        </div>
      </Drawer>
    </>
  );
}
